import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import i18next from 'i18next';

import Link from 'components/Link';
import TeasePicture from 'components/TeasePicture';
import ContentTimestamp from 'components/ContentTimestamp';
import Unibrow from 'components/Unibrow';

import { content as contentPropType } from 'lib/CustomPropTypes';
import { formatDuration } from 'lib/DateTime';
import { getIconForArticleType } from 'lib/iconUtils';
import { getTeasePicture } from 'lib/teaseUtils';
import { getDeepLink } from 'lib/deepLinking';
import { BreakpointContext } from 'lib/ContextTypes';

import AIMS_FLAVORS from 'lib/aimsFlavors';

// TODO: Update this image map to urls without hashes when the CMS supports that functionality
const FALLBACK_IMG_URL_MAP = {
  better: 'https://media4.s-nbcnews.com/i/newscms/2019_23/2888161/2x1_better_fallback_image_215abc36bbb66c1ffd105ebd4f190283.jpg',
  globalcitizen: 'https://media1.s-nbcnews.com/i/newscms/2019_23/2888166/2x1_global-citizen_fallback_image_215abc36bbb66c1ffd105ebd4f190283.jpg',
  knowyourvalue: 'https://media1.s-nbcnews.com/i/newscms/2019_23/2888221/2x1_know-your-value_fallback_image_0_1eb8627a141ee49daa31ae994fe99218.jpg',
  leftfield: 'https://media4.s-nbcnews.com/i/newscms/2019_23/2888176/2x1_left-field_fallback_image_215abc36bbb66c1ffd105ebd4f190283.jpg',
  mach: 'https://media3.s-nbcnews.com/i/newscms/2019_23/2888181/2x1_mach_fallback_image_215abc36bbb66c1ffd105ebd4f190283.jpg',
  msnbc: 'https://media2.s-nbcnews.com/i/newscms/2019_23/2888186/2x1_msnbc_fallback_image_215abc36bbb66c1ffd105ebd4f190283.jpg',
  news: 'https://media1.s-nbcnews.com/i/newscms/2019_23/2888216/2x1_nbcnews_fallback_image_0_cbf07ab7dd92887d74bd5e4ed1bde2e5.jpg',
  think: 'https://media2.s-nbcnews.com/i/newscms/2019_23/2888206/2x1_think_fallback_image_0_a5e44c6d5a0a2527b7c92c966f3fb003.jpg',
  today: 'https://media3.s-nbcnews.com/i/newscms/2019_23/2888211/2x1_today_fallback_image_0_a5e44c6d5a0a2527b7c92c966f3fb003.jpg',
};

function getUnibrowColor(unibrow, vertical) {
  if (!unibrow || !vertical) return null;

  const isThink = unibrow && unibrow.text && unibrow.text.toLowerCase() === 'opinion';
  return isThink ? 'bacon-cards-twobyone__unibrow__section-name--think' : '';
}

function headline(headlineText, url, vertical, target, deepLink) {
  return (
    <h3 className="bacon-cards-twobyone__header" data-test="two-by-one__headline__header">
      <Link to={url} className="bacon-cards-twobyone__header-link" data-test="two-by-one__headline__link" target={target}>
        {deepLink && <span className="bacon-cards-twobyone__header--deepLink-episode">{deepLink.episode}</span>}
        {headlineText}
      </Link>
    </h3>
  );
}

function getTeaseCategory(computedValues, item, type, deepLink, target) {
  const brow = computedValues && computedValues.unibrow
    ? { ...computedValues.unibrow }
    : {};
  // link to cananoical
  brow.url = { ...brow.url, primary: computedValues.url };

  if (type === 'slideshow') {
    // Show slide count
    const slideCount = item && item.slides && item.slides.length;
    brow.text = `${slideCount} IMAGES`;
  }

  if (type === 'video') {
    if (item && item.duration) {
      // Show video duration
      brow.text = formatDuration(item.duration);
    } else {
      brow.text = i18next.t('Watch');
    }
  }

  const iconClasses = `bacon-cards-twobyone__icon icon ${getIconForArticleType(type)}`;

  return (
    <div
      className="bacon-cards-twobyone__tease-category"
      data-test="two-by-one__tease-category"
    >
      <Unibrow
        className={classNames('bacon-cards-twobyone__eyebrow', type, { 'bacon-cards-twobyone__eyebrow-deep-link': deepLink })}
        unibrow={brow}
        data-test="two-by-one__tease-category__unibrow"
        deepLink={deepLink}
        target={target}
      />
      <div className="bacon-cards-twobyone__icon-container">
        <span className={iconClasses} data-test="two-by-one__tease-category__type-icon" />
        <span className="bacon-cards-twobyone__icon-background" />
      </div>
    </div>
  );
}

function getInfo(item, unibrow, options) {
  const { dateCreated, datePublished } = item;
  const { showEyebrow, showTimestamp, vertical } = options;

  const unibrowModifierClass = getUnibrowColor(unibrow, vertical);
  const fontStyles = 'bacon-cards-twobyone__font-style';
  const timestampStyles = 'bacon-cards-twobyone__timestamp-style';

  const delimiter = (
    <span
      className={classNames(
        fontStyles,
        timestampStyles,
      )}
      data-test="two-by-one__info__delimiter"
    >
      &nbsp;/&nbsp;
    </span>
  );

  return (
    <div>
      {
        showEyebrow && (
          <Unibrow
            className={classNames(
              fontStyles,
              unibrowModifierClass,
              'bacon-cards-twobyone__unibrow__section-name',
            )}
            unibrow={unibrow}
            data-test="two-by-one__info__unibrow"
          />
        )
      }
      { showEyebrow && showTimestamp && delimiter }
      {
        showTimestamp && (
          <ContentTimestamp
            dateCreated={dateCreated}
            datePublished={datePublished}
            dateOnly
            data-test="two-by-one__info__timestamp"
            styles={classNames(
              fontStyles,
              timestampStyles,
            )}
          />
        )
      }
    </div>
  );
}

function seasonDate(deepLink) {
  return (
    <span className="two-by-one__deepLink--season-date">
      <span className="two-by-one__deepLink--season">{deepLink.season}</span>
      <ContentTimestamp
        dateLabel="Air Date"
        dateOnly
        datePublished={deepLink.datePublished}
      />
    </span>
  );
}

function TwoByOne(props, context) {
  const {
    card: {
      computedValues = {},
      item: canonicalItem,
      type,
      id,
      isLazyLoaded = true,
    } = {},
    showEyebrow,
    showTimestamp,
    pkgType,
  } = props;

  const item = canonicalItem || {};
  const { isS: isMobile = false } = useContext(BreakpointContext);
  const deepLink = getDeepLink({ item, type }, pkgType, isMobile);
  const target = deepLink && '_blank';
  const { headline: headlineText = '', unibrow = {} } = computedValues;
  const { vertical } = context;
  const sizes = {
    s: AIMS_FLAVORS.FOCAL_440x220,
  };

  let { url } = computedValues;
  url = deepLink ? deepLink.url : url;

  return (
    <div
      className="bacon-cards-twobyone"
      data-test="two-by-one__container"
      data-contentid={id}
    >
      <Link
        to={url}
        className={classNames('bacon-cards-twobyone__tease-picture__link', { 'bacon-cards-twobyone__tease-picture__link-deep-link': deepLink })}
        data-test="two-by-one__tease-picture__link"
        target={target}
      >
        <TeasePicture
          computedValues={getTeasePicture(computedValues, FALLBACK_IMG_URL_MAP, vertical, 'ASPECT_2X1')}
          lazyload={isLazyLoaded}
          responsiveFlavors={sizes}
          type={type}
        />
        {
          getIconForArticleType(type)
          && getTeaseCategory(computedValues, item, type, deepLink, target)
        }
      </Link>
      { headline(headlineText, url, vertical, target, deepLink) }
      {deepLink
        ? seasonDate(deepLink)
        : getInfo(item, unibrow, { showEyebrow, showTimestamp, vertical })}
    </div>
  );
}

TwoByOne.contextTypes = {
  vertical: PropTypes.string,
};

TwoByOne.propTypes = {
  card: contentPropType,
  showEyebrow: PropTypes.bool,
  showTimestamp: PropTypes.bool,
  pkgType: PropTypes.string,
};

TwoByOne.defaultProps = {
  card: {},
  showEyebrow: true,
  showTimestamp: false,
  pkgType: null,
};

export default TwoByOne;
